import { useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { fetcher, mutation } from '../utils/api';
import { ApiRoutes } from '../constants/routes';
import { useAuthProvider } from '../utils/auth';

export const useLikes = () => {
  const queryClient = useQueryClient();
  const { user } = useAuthProvider();
  
  // Debug logging
  useEffect(() => {
    console.log('useLikes: User state changed:', { 
      user: !!user, 
      userId: user?.id || user?.uuid,
      userObject: user 
    });
  }, [user]);
  
  // Get user's likes - enable when user is authenticated
  const { data: userLikes, isLoading: isLoadingLikes, error: likesError } = useQuery({
    queryKey: ['user-likes'],
    queryFn: async () => {
      console.log('useLikes: Fetching user likes...');
      try {
        const response = await fetcher(ApiRoutes.UserLikes);
        console.log('useLikes: User likes response:', response);
        return response;
      } catch (error) {
        console.error('useLikes: Error fetching user likes:', error);
        throw error;
      }
    },
    enabled: !!user, // Only fetch when user is authenticated
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });

  // Log any errors
  useEffect(() => {
    if (likesError) {
      console.error('useLikes: Query error:', likesError);
    }
  }, [likesError]);

  // Toggle like mutation
  const toggleLikeMutation = useMutation({
    mutationFn: async ({ contentType, objectId }) => {
      console.log('useLikes: Toggling like:', { contentType, objectId });
      try {
        const response = await mutation(ApiRoutes.LikeToggle, {
          content_type: contentType,
          object_id: objectId,
        }, 'POST');
        console.log('useLikes: Toggle response:', response);
        return response;
      } catch (error) {
        console.error('useLikes: Error toggling like:', error);
        throw error;
      }
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch relevant queries
      const { contentType, objectId } = variables;
      
      console.log('useLikes: Like toggled successfully, invalidating queries');
      
      // Invalidate user likes
      queryClient.invalidateQueries(['user-likes']);
      
      // Invalidate specific content likes
      queryClient.invalidateQueries(['content-likes', contentType, objectId]);
      
      // Invalidate content lists that might show like status
      queryClient.invalidateQueries(['tracks']);
      queryClient.invalidateQueries(['albums']);
      queryClient.invalidateQueries(['acts']);
    },
    onError: (error, variables) => {
      console.error('useLikes: Mutation error:', error, variables);
    }
  });

  // Check if specific content is liked
  const isLiked = useCallback((contentType, objectId) => {
    if (!userLikes?.likes || !user) {
      console.log('useLikes: isLiked check failed:', { 
        hasUserLikes: !!userLikes?.likes, 
        hasUser: !!user,
        contentType,
        objectId 
      });
      return false;
    }
    
    // Convert objectId to string for comparison since backend might return integers
    const objectIdStr = String(objectId);
    const liked = userLikes.likes[contentType]?.some(id => String(id) === objectIdStr) || false;
    
    console.log('useLikes: isLiked result:', { 
      contentType, 
      objectId, 
      objectIdStr,
      liked, 
      userLikes: userLikes.likes,
      availableIds: userLikes.likes[contentType]
    });
    return liked;
  }, [userLikes, user]);

  // Toggle like for specific content
  const toggleLike = useCallback(async (contentType, objectId) => {
    if (!user) {
      console.warn('useLikes: User must be authenticated to like content');
      return false;
    }
    
    try {
      await toggleLikeMutation.mutateAsync({ contentType, objectId });
      return true;
    } catch (error) {
      console.error('useLikes: Failed to toggle like:', error);
      return false;
    }
  }, [toggleLikeMutation, user]);

  // Get like count for specific content
  const getLikeCount = useCallback(async (contentType, objectId) => {
    try {
      const response = await fetcher(`${ApiRoutes.ContentLikes}${contentType}/${objectId}/`);
      return response.total_likes || 0;
    } catch (error) {
      console.error('useLikes: Failed to get like count:', error);
      return 0;
    }
  }, []);

  // Fetch user likes (call this when user is authenticated)
  const fetchUserLikes = useCallback(async () => {
    if (user) {
      console.log('useLikes: Manually fetching user likes');
      try {
        // Invalidate and refetch the query
        await queryClient.invalidateQueries(['user-likes']);
        // Force a refetch
        await queryClient.refetchQueries(['user-likes']);
        console.log('useLikes: User likes refetched successfully');
      } catch (error) {
        console.error('useLikes: Error refetching user likes:', error);
      }
    } else {
      console.log('useLikes: No user authenticated, cannot fetch likes');
    }
  }, [queryClient, user]);

  return {
    userLikes: userLikes?.likes || {},
    isLoadingLikes,
    isLiked,
    toggleLike,
    getLikeCount,
    fetchUserLikes,
    isToggling: toggleLikeMutation.isLoading,
    isAuthenticated: !!user,
    error: likesError,
  };
};
